/**
 * Collections Hooks
 *
 * React Query hooks for collection management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { collectionsApi, artworksApi } from '../api/easelApi'
import { useToast } from '@/hooks/use-toast'
import type { CreateCollectionData, UpdateCollectionData } from '../types'

// =============================================================================
// Query Keys
// =============================================================================

export const collectionKeys = {
  all: ['collections'] as const,
  lists: () => [...collectionKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...collectionKeys.lists(), filters] as const,
  details: () => [...collectionKeys.all, 'detail'] as const,
  detail: (id: number) => [...collectionKeys.details(), id] as const,
}

// =============================================================================
// Collections Hooks
// =============================================================================

export function useCollections(params?: {
  featured?: boolean
  visible?: boolean
  limit?: number
  offset?: number
}) {
  return useQuery({
    queryKey: collectionKeys.list(params || {}),
    queryFn: () => collectionsApi.getCollections(params),
  })
}

export function useCollection(id: number) {
  return useQuery({
    queryKey: collectionKeys.detail(id),
    queryFn: () => collectionsApi.getCollection(id),
    enabled: !!id,
  })
}

/**
 * List the signed-in artist's artworks — used to populate the collection
 * "Add Artworks" picker.
 */
export function useMyArtworks(enabled = true) {
  return useQuery({
    queryKey: ['easel', 'my-artworks'],
    queryFn: () => artworksApi.listMine(),
    enabled,
  })
}

export function useCreateCollection() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateCollectionData) => collectionsApi.createCollection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists() })
      toast({
        title: 'Collection Created',
        description: 'Your collection has been created successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create collection',
        variant: 'destructive',
      })
    },
  })
}

export function useUpdateCollection() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCollectionData }) =>
      collectionsApi.updateCollection(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists() })
      toast({
        title: 'Collection Updated',
        description: 'Your collection has been updated successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update collection',
        variant: 'destructive',
      })
    },
  })
}

export function useDeleteCollection() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: number) => collectionsApi.deleteCollection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists() })
      toast({
        title: 'Collection Deleted',
        description: 'Your collection has been removed.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete collection',
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Collection Artworks Hooks
// =============================================================================

export function useAddArtworkToCollection() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      collectionId,
      artworkId,
      displayOrder,
    }: {
      collectionId: number
      artworkId: number
      displayOrder?: number
    }) => collectionsApi.addArtwork(collectionId, artworkId, displayOrder),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(variables.collectionId) })
      toast({
        title: 'Artwork Added',
        description: 'The artwork has been added to the collection.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add artwork to collection',
        variant: 'destructive',
      })
    },
  })
}

export function useRemoveArtworkFromCollection() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ collectionId, artworkId }: { collectionId: number; artworkId: number }) =>
      collectionsApi.removeArtwork(collectionId, artworkId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(variables.collectionId) })
      toast({
        title: 'Artwork Removed',
        description: 'The artwork has been removed from the collection.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove artwork from collection',
        variant: 'destructive',
      })
    },
  })
}

export function useReorderCollectionArtworks() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ collectionId, artworkIds }: { collectionId: number; artworkIds: number[] }) =>
      collectionsApi.reorderArtworks(collectionId, artworkIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(variables.collectionId) })
    },
  })
}
